import React from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Card, Icon, Text } from '@zillow/constellation-10';
import { IconHomesFilled } from '@zillow/constellation-icons';

import { SpinnerContainer } from '@zg-rentals/rental-manager-env';

import { AppSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import EmptyState from '../../EmptyState';
import { NoSearchResults } from '../NoSearchResults';
import PropertiesViewGrid from '../PropertiesViewGrid';
import PropertiesViewList from '../PropertiesViewList';
import { ZRMOverview } from '../ZRMOverview';

import type { PropertyProps } from '../../../model/Property';

export type Props = {
  listings: Array<PropertyProps>;
  showArchived: boolean;
  isLoading: boolean;
  didPropertiesLoad: boolean;
  isGetPropertiesError?: boolean;
  onRemoveSearchTextClick?: (value: string) => void;
  hasAnyArchivedListings: boolean;
  toggleArchivedView: () => void;
};

const PropertiesDisplay = ({
  listings,
  showArchived,
  isLoading,
  didPropertiesLoad,
  isGetPropertiesError = false,
  onRemoveSearchTextClick = () => {},
  hasAnyArchivedListings,
  toggleArchivedView,
}: Props) => {
  const { isScreenLgGTE, user, propertiesFilters } = useSelector(
    (state) => ({
      isScreenLgGTE: AppSelectors.makeIsScreenLgGTE(state),
      user: state.user,
      propertiesFilters: state.properties.propertiesFilters,
    }),
    shallowEqual,
  );

  const renderLoadingDisplay = () => {
    return <SpinnerContainer height="300px" />;
  };

  const renderTimedOutDisplay = () => (
    <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginTop: '1200' }}>
      <Text
        css={{
          textAlign: 'center',
          padding: { base: '0 {spacing.400}', lg: '0' },
          marginBottom: '800',
          marginInline: '400',
        }}
      >
        We are having trouble retrieving your properties at this time. Please refresh the page or check back soon.
      </Text>
    </Box>
  );

  const renderNoListingsFound = () => {
    if (propertiesFilters.searchText) {
      return <NoSearchResults showArchived={showArchived} onRemoveSearchTextClick={onRemoveSearchTextClick} />;
    }

    if (showArchived) {
      return (
        <EmptyState
          icon={<Icon render={<IconHomesFilled />} size="md" css={{ color: 'icon.subtle' }} />}
          heading="No archived properties"
          headingLevel={3}
          align="center"
          useCard={false}
        />
      );
    }

    if (user.features.canAddListing) {
      return (
        <ZRMOverview hasAnyArchivedListings={hasAnyArchivedListings} handleShowArchivedClick={toggleArchivedView} />
      );
    }

    return (
      <EmptyState
        icon={<Icon render={<IconHomesFilled />} size="md" css={{ color: 'icon.subtle' }} />}
        heading="No properties found"
        headingLevel={3}
        align="center"
        useCard={false}
      />
    );
  };

  const renderView = () =>
    isScreenLgGTE ? <PropertiesViewList listings={listings} /> : <PropertiesViewGrid listings={listings} />;

  const renderListingsDisplay = () => (
    <React.Fragment>
      {listings.length > 0 ? (
        renderView()
      ) : (
        <Card
          outlined={false}
          elevated={false}
          css={{
            marginBlock: '1200',
            backgroundColor: 'transparent',
            textAlign: 'center',
            lg: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' },
          }}
        >
          {renderNoListingsFound()}
        </Card>
      )}
    </React.Fragment>
  );

  const renderContent = () => {
    if (!didPropertiesLoad && isLoading) {
      return renderLoadingDisplay();
    }

    if (isGetPropertiesError) {
      return renderTimedOutDisplay();
    }

    return renderListingsDisplay();
  };

  return <Box data-testid="PropertiesDisplay">{renderContent()}</Box>;
};

export { PropertiesDisplay };
export default PropertiesDisplay;
